/**
 * Weeks grid visualization component
 */

import { LifeStats } from '../utils/calculations';

export interface WeeksGridOptions {
    stats: LifeStats;
    animationDelay?: number; // ms delay between each week animation
}

/**
 * Create the weeks grid visualization
 */
export function createWeeksGrid(options: WeeksGridOptions): HTMLElement {
    const { stats, animationDelay = 2 } = options;
    const container = document.createElement('div');
    container.className = 'weeks-section';

    // Weeks container with glass effect
    const weeksContainer = document.createElement('div');
    weeksContainer.className = 'weeks-container glass-card';

    // Legend
    const legend = document.createElement('div');
    legend.className = 'weeks-legend';
    legend.innerHTML = `
    <div class="legend-item">
      <span class="legend-dot lived"></span>
      <span>Weeks lived</span>
    </div>
    <div class="legend-item">
      <span class="legend-dot current"></span>
      <span>This week</span>
    </div>
    <div class="legend-item">
      <span class="legend-dot remaining"></span>
      <span>Weeks remaining</span>
    </div>
  `;
    weeksContainer.appendChild(legend);

    // Grid with year labels
    const gridWithLabels = document.createElement('div');
    gridWithLabels.className = 'grid-with-labels';

    // Year labels
    const yearLabels = document.createElement('div');
    yearLabels.className = 'year-labels';

    const totalYears = Math.ceil(stats.totalWeeks / 52);
    for (let year = 0; year <= totalYears; year += 10) {
        const label = document.createElement('div');
        label.textContent = year.toString();
        label.style.height = `${10 * (10 + 2)}px`; // 10 weeks * (size + gap)
        label.style.display = 'flex';
        label.style.alignItems = 'flex-start';
        yearLabels.appendChild(label);
    }

    // Weeks grid
    const grid = document.createElement('div');
    grid.className = 'weeks-grid';

    // Generate weeks
    for (let i = 0; i < stats.totalWeeks; i++) {
        const week = document.createElement('div');
        week.className = 'week';

        // Determine week state
        if (i < stats.weeksLived - 1) {
            week.classList.add('lived');
        } else if (i === stats.weeksLived - 1) {
            week.classList.add('current');
        } else {
            week.classList.add('remaining');
        }

        // Add tooltip
        const weekNumber = (i % 52) + 1;
        const yearOfLife = Math.floor(i / 52) + 1;
        week.title = `Year ${yearOfLife}, Week ${weekNumber}`;

        // Staggered animation
        week.style.animationDelay = `${i * animationDelay}ms`;

        grid.appendChild(week);
    }

    gridWithLabels.appendChild(yearLabels);
    gridWithLabels.appendChild(grid);
    weeksContainer.appendChild(gridWithLabels);
    container.appendChild(weeksContainer);

    return container;
}

/**
 * Create a simplified weeks grid for smaller displays
 */
export function createCompactWeeksGrid(stats: LifeStats): HTMLElement {
    const container = document.createElement('div');
    container.className = 'compact-weeks-section';

    // Progress bar instead of full grid
    const progressContainer = document.createElement('div');
    progressContainer.className = 'progress-container glass-card';
    progressContainer.style.padding = 'var(--space-6)';

    const progressBar = document.createElement('div');
    progressBar.style.cssText = `
    height: 24px;
    background: var(--week-remaining);
    border-radius: var(--radius-full);
    overflow: hidden;
    position: relative;
  `;

    const progressFill = document.createElement('div');
    progressFill.style.cssText = `
    height: 100%;
    background: linear-gradient(90deg, var(--accent), var(--accent-hover));
    border-radius: var(--radius-full);
    width: ${stats.percentLived}%;
    transition: width 1s ease;
  `;

    const progressLabel = document.createElement('div');
    progressLabel.style.cssText = `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--foreground);
  `;
    progressLabel.textContent = `${stats.percentLived.toFixed(1)}% of life lived`;

    progressBar.appendChild(progressFill);
    progressBar.appendChild(progressLabel);
    progressContainer.appendChild(progressBar);
    container.appendChild(progressContainer);

    return container;
}
